import { Button, Card, Col, Descriptions, Form, Input, Row, Spin } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useContext, useEffect, useState } from 'react';
import { API } from '../../api';
import { UserContext } from '../../context/UserProvider';
import { notifyError, notifySuccess } from '../../store/utils';

// backend-ийн StrongPassword-тэй ижил дүрэм
const PASSWORD_RULE = /^(?=.*[a-z])(?=.*[A-Z]).{6,}$/;

const Profile = () => {
  const { onChange } = useContext(UserContext);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const { result } = await API.get({ apiVersion: 'api' })('/auth/profile');
      setProfile(result);
      profileForm.setFieldsValue({
        fname: result?.fname,
        lname: result?.lname,
        phone: result?.phone,
      });
    } catch (error: any) {
      notifyError(error.message);
    } finally {
      setLoading(false);
    }
  }, [profileForm]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const saveProfile = async (values: any) => {
    setSavingProfile(true);
    try {
      const { result } = await API.put({ apiVersion: 'api' })('/auth/profile', values);
      setProfile(result);
      notifySuccess('Мэдээлэл хадгалагдлаа');
      // header дээрх хэрэглэгчийн мэдээллийг шинэчилнэ
      const info = await API.get({ apiVersion: 'api' })('/auth/info');
      if (info?.result?.member) onChange(info.result.member);
    } catch (error: any) {
      notifyError(error.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (values: any) => {
    setSavingPassword(true);
    try {
      await API.post({ apiVersion: 'api' })('/auth/password', {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      notifySuccess('Нууц үг амжилттай солигдлоо');
      passwordForm.resetFields();
    } catch (error: any) {
      notifyError(error.message);
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <Spin />
      </Card>
    );
  }

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24}>
        <Card title="Хэрэглэгчийн мэдээлэл">
          <Descriptions column={{ xs: 1, sm: 2, lg: 3 }} size="small">
            <Descriptions.Item label="Нэвтрэх нэр">{profile?.email}</Descriptions.Item>
            <Descriptions.Item label="Эрх">{profile?.roleName}</Descriptions.Item>
            {profile?.customerName && (
              <Descriptions.Item label="Харилцагч">{profile.customerName}</Descriptions.Item>
            )}
            {profile?.branchName && (
              <Descriptions.Item label="Салбар">{profile.branchName}</Descriptions.Item>
            )}
            <Descriptions.Item label="Бүртгэсэн огноо">
              {profile?.createdAt ? dayjs(profile.createdAt).format('YYYY-MM-DD') : ''}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card title="Үндсэн мэдээлэл засах">
          <Form form={profileForm} layout="vertical" onFinish={saveProfile}>
            <Form.Item
              label="Нэр"
              name="fname"
              rules={[{ required: true, whitespace: true, message: 'Нэр оруулна уу' }]}
            >
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item label="Овог" name="lname">
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item
              label="Утасны дугаар"
              name="phone"
              rules={[
                { required: true, whitespace: true, message: 'Утас оруулна уу' },
                { pattern: /^[0-9+\-\s]{6,20}$/, message: 'Утасны дугаар буруу байна' },
              ]}
            >
              <Input maxLength={20} inputMode="tel" />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={savingProfile}>
              Хадгалах
            </Button>
          </Form>
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card title="Нууц үг солих">
          <Form form={passwordForm} layout="vertical" onFinish={changePassword}>
            <Form.Item
              label="Одоогийн нууц үг"
              name="currentPassword"
              rules={[{ required: true, message: 'Одоогийн нууц үгээ оруулна уу' }]}
            >
              <Input.Password autoComplete="current-password" />
            </Form.Item>
            <Form.Item
              label="Шинэ нууц үг"
              name="newPassword"
              rules={[
                { required: true, message: 'Шинэ нууц үг оруулна уу' },
                {
                  pattern: PASSWORD_RULE,
                  message:
                    'Нууц үг 6-аас дээш тэмдэгттэй, том үсэг, жижиг үсэг агуулсан байх ёстой',
                },
              ]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Form.Item
              label="Шинэ нууц үг давтах"
              name="confirmPassword"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: 'Шинэ нууц үгээ давтан оруулна уу' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Нууц үг таарахгүй байна'));
                  },
                }),
              ]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={savingPassword}>
              Нууц үг солих
            </Button>
          </Form>
        </Card>
      </Col>
    </Row>
  );
};

export default Profile;
